/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business セレクトボックスコンポーネント。ラベル・エラー・ヘルプテキスト・プレースホルダー・3段階サイズ・カスタム矢印を統合。aria-describedby は呼び出し側指定と内部の error / helper 記述 id を併合する（上書きしない）
 * @graph-connects none
 */
import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { cn } from '../../../lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** ラベル */
  label?: string;
  /** エラーメッセージ */
  error?: string;
  /** ヘルプテキスト */
  helperText?: string;
  /** 選択肢 */
  options: SelectOption[];
  /** プレースホルダー */
  placeholder?: string;
  /** 入力サイズ */
  size?: 'sm' | 'md' | 'lg';
  /** 全幅表示 */
  fullWidth?: boolean;
}

/** @graph-connects none */
const sizeStyles = {
  sm: 'px-3 py-1.5 text-sm pr-8',
  md: 'px-4 py-2 text-sm pr-10',
  lg: 'px-4 py-3 text-base pr-10',
};

/** @graph-connects none */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      options,
      placeholder,
      size = 'md',
      fullWidth = false,
      className,
      id,
      disabled,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const selectId = id ?? `select-${generatedId}`;
    const hasError = Boolean(error);
    // 内部の error / helper の記述 id と、呼び出し側が渡した aria-describedby を **併合** する
    // （上書きしない）。urgency ガイド等の外部記述を関連づけつつ、error 併用時にエラー id を潰さない。
    const describedBy =
      [hasError ? `${selectId}-error` : helperText ? `${selectId}-helper` : null, ariaDescribedBy]
        .filter(Boolean)
        .join(' ') || undefined;

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            className={cn(
              'w-full appearance-none bg-[var(--color-bg-surface)] border rounded-lg text-[var(--color-text-primary)] transition-all duration-[var(--transition-fast)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--color-bg-base)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer',
              hasError
                ? 'border-[var(--color-error)] focus:ring-[var(--color-error)]'
                : 'border-[var(--color-border)] hover:border-[var(--color-border-hover)] focus:border-[var(--color-border-focus)] focus:ring-[var(--color-border-focus)]',
              sizeStyles[size],
              className,
            )}
            aria-invalid={hasError}
            aria-describedby={describedBy}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          {/* Dropdown arrow */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--color-text-muted)]">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        {error && (
          <p id={`${selectId}-error`} className="mt-1.5 text-sm text-[var(--color-error)]">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${selectId}-helper`} className="mt-1.5 text-sm text-[var(--color-text-muted)]">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';
